import {
    ActualAmounts,
    PaymentDifference,
    PaymentDifferenceState,
    ShiftCloseoutPaymentMethod,
    ShiftCloseoutReconciliation,
} from '@/features/shiftCloseouts/types'
import { parseMoneyInput } from '@/features/shiftCloseouts/utils/moneyInput'
import currencyJs from 'currency.js'
import invariant from 'tiny-invariant'

const getDifferenceState = (amount: string): PaymentDifferenceState => {
    const value = currencyJs(amount)

    if (value.value < 0) return 'shortage'
    if (value.value > 0) return 'surplus'

    return 'balanced'
}

export const calculateReconciliation = (
    currency: string,
    paymentMethods: ShiftCloseoutPaymentMethod[],
    actualAmounts: Partial<ActualAmounts>
): ShiftCloseoutReconciliation => {
    const differences: Partial<Record<string, PaymentDifference>> = {}
    let totalDifference = currencyJs(0)
    let isComplete = true

    paymentMethods.forEach(({ expectedAmount, identifier, mode, paymentType }) => {
        invariant(expectedAmount.currency === currency, 'Currencies mismatch')

        const actualAmount =
            mode === 'automatic' || !paymentType
                ? expectedAmount.amount
                : parseMoneyInput(actualAmounts[paymentType] ?? '')

        if (!actualAmount) {
            isComplete = false
            return
        }

        const differenceAmount = currencyJs(actualAmount).subtract(expectedAmount.amount).toString()

        differences[identifier] = {
            amount: { amount: differenceAmount, currency },
            state: getDifferenceState(differenceAmount),
        }
        totalDifference = totalDifference.add(differenceAmount)
    })

    const isBalanced =
        isComplete &&
        paymentMethods.every(({ identifier }) => differences[identifier]?.state === 'balanced')

    let state: ShiftCloseoutReconciliation['state'] = 'incomplete'

    if (isComplete) {
        if (isBalanced) state = 'balanced'
        else if (totalDifference.value < 0) state = 'shortage'
        else if (totalDifference.value > 0) state = 'surplus'
        else state = 'mismatch'
    }

    return {
        differences,
        isBalanced,
        isComplete,
        state,
        totalDifference: { amount: totalDifference.toString(), currency },
    }
}
